import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchResources, updateResource, fetchUnassignedRoomSummary } from "@common/lib/api/resources";
import { Resource } from "@common/utils/types";
import { useAccountContext } from "@/state/providers/AccountProvider";
import * as Sentry from "@sentry/react";

const getResourcesFromQueryData = (queryData: { data?: unknown } | null | undefined): Resource[] => {
    return Array.isArray(queryData?.data) ? queryData.data : [];
};

const getTotalFromQueryData = (queryData: { total?: unknown } | null | undefined): number => {
    return typeof queryData?.total === "number" ? queryData.total : 0;
};

export type ResourcePaginationModel = {
    page: number; // 1-based
    pageSize: number;
};

export const RESOURCES_QUERY_KEY = ["resources"] as const;

/**
 * React Query hook for managing resources with server-side pagination.
 * Router-agnostic: accepts page/pageSize/search as plain params so it can be
 * used in tests and non-Router contexts. URL-sync lives in ResourceTable.
 */
export const useResources = (params?: { page?: number; pageSize?: number; search?: string }) => {
    const queryClient = useQueryClient();
    const { accountData } = useAccountContext();
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && accountData.id
            ? String(accountData.id)
            : undefined;

    const page = Math.max(1, params?.page ?? 1);
    const pageSize = Math.max(1, params?.pageSize ?? 50);
    const search = params?.search ?? "";

    const queryKey = ["resources", clinicAccountId, page, pageSize, search] as const;

    // Query for fetching resources (scoped to resolved clinic account, with pagination and search)
    const query = useQuery({
        queryKey,
        queryFn: () =>
            fetchResources({
                page,
                limit: pageSize,
                search: search || undefined,
            }),
        enabled: Boolean(clinicAccountId),
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        placeholderData: (prev) => prev,
    });

    // Mutation for updating a resource
    const updateMutation = useMutation({
        mutationFn: ({ id, resource }: { id: string; resource: Partial<Resource> }) => updateResource(id, resource),
        onSuccess: () => {
            // On success, refresh all resource cache entries so server truth is reflected.
            // On failure, callers that use optimistic updates perform their own rollback via
            // setQueryData(previousData) — no additional invalidation needed on the error path.
            // Guard: skip invalidation if clinicAccountId is not yet resolved — an undefined
            // second segment would match every 'resources' entry across all accounts.
            if (!clinicAccountId) return;
            queryClient.invalidateQueries({ queryKey: ["resources", clinicAccountId] });
        },
    });

    // Search all cached pages for this account — not just the currently displayed page.
    const getResourceById = (id: string | null | undefined): Resource | null => {
        if (!id) return null;
        const allPages = queryClient.getQueriesData<{ data: Resource[]; total: number }>({
            queryKey: [RESOURCES_QUERY_KEY[0], clinicAccountId],
        });
        for (const [, pageData] of allPages) {
            const found = getResourcesFromQueryData(pageData).find((r) => r.id === id);
            if (found) return found;
        }
        return null;
    };

    return {
        query,
        queryKey,
        resources: getResourcesFromQueryData(query.data),
        total: getTotalFromQueryData(query.data),
        updateMutation,
        getResourceById,
        clinicAccountId,
    };
};

// The resources API caps pageSize at 1000. No real clinic has anywhere near 1000 exam rooms.
// If this limit is ever hit, the Sentry capture below will alert us.
const RESOURCE_FETCH_LIMIT = 1000;

/**
 * Lightweight hook for non-paginated access (calendar column mapping).
 * Fetches up to RESOURCE_FETCH_LIMIT resources with a stable query key so all callers share one cache entry.
 */
export const useAllResources = (options?: { enabled?: boolean }) => {
    const { accountData } = useAccountContext();
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && accountData.id
            ? String(accountData.id)
            : undefined;

    const query = useQuery({
        queryKey: ["resources", clinicAccountId, "all"],
        queryFn: () => fetchResources({ page: 1, limit: RESOURCE_FETCH_LIMIT }),
        enabled: Boolean(clinicAccountId) && (options?.enabled ?? true),
        staleTime: 2 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        select: (data) => {
            const items = data.data ?? [];
            if (typeof data.total === "number" && data.total > items.length) {
                const msg = `useAllResources: fetched ${items.length} of ${data.total} — limit exceeded. Increase RESOURCE_FETCH_LIMIT.`;
                console.warn(msg);
                Sentry.captureMessage(msg, "warning");
            }
            return items;
        },
    });

    return query;
};

/**
 * Room Settings banner data — counts of appointments Unassigned because their room is
 * excluded from sync, grouped by room. Live query, no snapshot: re-fetches whenever the
 * settings page mounts, so re-including a room clears the banner on the next visit with no
 * extra invalidation logic needed.
 */
export const useUnassignedRoomSummary = () => {
    const { accountData } = useAccountContext();
    const clinicAccountId =
        accountData && typeof accountData === "object" && "id" in accountData && accountData.id
            ? String(accountData.id)
            : undefined;

    return useQuery({
        queryKey: ["resources", clinicAccountId, "unassigned-summary"],
        queryFn: fetchUnassignedRoomSummary,
        enabled: Boolean(clinicAccountId),
        staleTime: 2 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
};
